import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Bell, BellSlash } from "@phosphor-icons/react";
import { getPushSubscriptionState, subscribeToPush, unsubscribeFromPush } from "@/serviceWorkerRegistration";

/**
 * PushToggle — opt-in Web Push subscription button.
 * Shows: not supported → Bell with red dot, off → "Activar", on → "Desactivar".
 */
export default function PushToggle({ compact = false }) {
  const { t } = useTranslation();
  const [state, setState] = useState({ supported: false, subscribed: false });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    getPushSubscriptionState().then(setState).catch(() => {});
  }, []);

  if (!state.supported) return null;

  const toggle = async () => {
    setLoading(true);
    setError("");
    try {
      if (state.subscribed) {
        await unsubscribeFromPush();
        setState({ supported: true, subscribed: false });
      } else {
        await subscribeToPush();
        setState({ supported: true, subscribed: true });
      }
    } catch (e) {
      setError(e.message || "Erro");
    } finally {
      setLoading(false);
    }
  };

  const Icon = state.subscribed ? Bell : BellSlash;
  return (
    <button
      data-testid="push-toggle"
      data-pushstate={state.subscribed ? "on" : "off"}
      onClick={toggle}
      disabled={loading}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: compact ? "5px 10px" : "8px 14px",
        borderRadius: 9999,
        border: `1px solid ${state.subscribed ? "var(--primary)" : "var(--border-color)"}`,
        background: state.subscribed ? "rgba(45,74,62,0.08)" : "var(--surface)",
        color: state.subscribed ? "var(--primary)" : "var(--text-main)",
        fontFamily: "Outfit",
        fontWeight: 600,
        fontSize: compact ? "0.72rem" : "0.82rem",
        cursor: loading ? "default" : "pointer",
        opacity: loading ? 0.6 : 1,
      }}
      title={error || (state.subscribed ? t("push.disable") : t("push.enable"))}
    >
      <Icon size={compact ? 12 : 14} weight={state.subscribed ? "fill" : "regular"} />
      {state.subscribed ? t("push.on") : t("push.off")}
    </button>
  );
}
